import { Box } from "@mui/material";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { LocationDataType } from "../../../../dummy-data/location-data";
import useDisclosure from "../../../../hooks/use-disclosure";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_UUID_NAME_LIST,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getAddressFormat,
  getUnNecessaryTransformData,
  transformMultiSelectFrontEndVal,
  unNecessaryFEOfficeHourTrans,
} from "../../../../utils/utils";
import ProviderLocationIdLayoutModal from "./ProviderLocationIdLayout";
import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import Text from "../../../../components/Text";
import ActionMenu from "../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../components/app/DeleteModal";
import LocationsDrawer from "../../../../components/app/admin/provider-groups/LocationsDrawer";
import { STATE_RECORD_FULL_NAME_MAPPER } from "../../../../constants";
import { useProviderGroupLocationUpdate } from "../../../../queries/admin/provider-group/use-pg-location-update";
import { useLocationStatus } from "../../../../queries/use-location-status";
import { useLocationsList } from "../../../../queries/use-locationList";
import { useLocationArchive } from "../../../../queries/use-location-archive";
import MultiSpecialty from "../../../../components/MultiSpecialty";
import { GridSortItem } from "@mui/x-data-grid";
import { useLocation } from "../../../../queries/use-location";
import { useQueryClient } from "@tanstack/react-query";
import { useLoadingContext } from "../../../../context/LoadingProvider";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

const ProviderGroupLocations = () => {
  const {
    searchKey,
    stateFilter,
    groupNpi,
    pgName,
    profileData,
  }: {
    searchKey: string;
    stateFilter: string[];
    groupNpi?: string;
    pgName: any;
    profileData: any;
  } = useOutletContext();

  const realm = getStoredRealm();
  const viewAs = getViewAs();

  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [locationUuid, setLocationUuid] = useState<string>();

  const params = useParams();
  const { mutateAsync: changeLocationStatus } = useLocationStatus();
  const {
    data: providerGroupLocationList,
    refetch,
    isLoading: tableDataLoading,
    isFetching,
  } = useLocationsList({
    gridSorting,
    pagination,
    searchKey,
    stateFilter,
    providerGroupId:
      params.providerGroupId ?? (viewAs === "provider" ? realm : undefined),
  });
  const { data: rawLocationDetails, isFetching: isLocationFetching } =
    useLocation({
      locationId: locationUuid as any,
      providerGroupId:
        params.providerGroupId ??
        ((viewAs === "provider" ? realm : undefined) as any),
    });

  const { mutateAsync: updateLocation } = useProviderGroupLocationUpdate();
  const { mutateAsync: archiveLocation } = useLocationArchive();
  const locationDetails: any = rawLocationDetails?.data;
  const queryClient = useQueryClient();
  const { setIsLoading } = useLoadingContext();

  function editLocation(values: any) {
    const transFormData: any = getUnNecessaryTransformData(
      ["specialities"],
      values
    );
    return updateLocation({
      location: transFormData as any,
      xTENANTID: params?.providerGroupId,
    }).then((res) => {
      queryClient.invalidateQueries({
        queryKey: ["provider-group-location-by-id"],
      });
      return res;
    });
  }

  const {
    opened: openedLocationModal,
    open: openLocationModal,
    close: closeLocationsModal,
  } = useDisclosure(false);

  const { opened, open, close } = useDisclosure(false);

  useEffect(() => {
    setIsLoading(opened && isLocationFetching);
    return () => setIsLoading(false);
  }, [opened, isLocationFetching]);
  const {
    opened: openedDeleteModal,
    open: openDeleteModal,
    close: closeDeleteModal,
    deleteId,
  } = useDeleteDisClosure(false);

  const COLUMNS = applyDefaultColumnSettings<LocationDataType>([
    {
      field: "locationId",
      flex: 0.7,
      minWidth: 110,
      headerName: "Location ID",
      sortable: false,
      renderCell: ({ ...params }) => {
        return (
          <Text
            isTabletext
            onClick={() => {
              setLocationUuid(params.row?.uuid);
              openLocationModal();
            }}
            style={{ cursor: "pointer" }}
            c="primary.main"
          >
            {params.value || "-"}
          </Text>
        );
      },
    },
    {
      field: "name",
      flex: 1.3,
      minWidth: 120,
      headerName: "Location Name",
      sortable: false,
      renderCell: ({ ...params }) => {
        return (
          <Text
            isTabletext
            onClick={() => {
              setLocationUuid(params.row?.uuid);
              openLocationModal();
            }}
            style={{ cursor: "pointer" }}
            c="primary.main"
          >
            {params.value || "-"}
          </Text>
        );
      },
    },
    {
      field: "specialities",
      flex: 1.2,
      minWidth: 140,
      headerName: "Specialties",
      sortable: false,
      renderCell: ({ value }) =>
        value ? <MultiSpecialty specialties={value} width={280} /> : "-",
    },
    {
      field: "physicalAddressLine",
      flex: 1.6,
      minWidth: 140,
      headerName: "Address",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Text isTabletext>
            {!!row?.physicalAddress
              ? getAddressFormat(row?.physicalAddress as any)
              : "-"}
          </Text>
        );
      },
    },
    {
      field: "physicalAddressCity",
      flex: 0.8,
      // minWidth: 100,
      headerName: "City",
      sortable: false,
      renderCell: ({ row }) => row?.physicalAddress?.city ?? "-",
    },
    {
      field: "physicalAddressState",
      flex: 0.8,
      // minWidth: 140,
      headerName: "State",
      sortable: false,
      renderCell: ({ row }) =>
        STATE_RECORD_FULL_NAME_MAPPER[row?.physicalAddress?.state] ?? "-",
    },
    {
      field: "physicalAddress",
      flex: 0.8,
      minWidth: 80,
      headerName: "ZIP Code",
      sortable: false,
      renderCell: ({ value }) => value?.zipcode ?? "-",
    },
    {
      field: "contact",
      flex: 0.8,
      minWidth: 120,
      headerName: "Contact Number",
      sortable: false,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "active",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row?.uuid?.toString()}
          checked={value}
          onConfirm={(status, id) =>
            changeLocationStatus({
              locationId: id as string,
              status,
              xTENANTID: params.providerGroupId,
            }).then((res) => {
              refetch();
              return res;
            })
          }
        />
      ),
    },
    // { future reference while req change to edit only icon
    //   field: "Action",
    //   width: 90,
    //   headerClassName: "header-theme",
    //   hideSortIcons: true,
    //   renderCell: ({ row }: any) => (

    //     <IconButton onClick={() => handleOpenEditProfileModal(row)}>
    //       <ModeEditOutlineOutlinedIcon color="primary" />
    //     </IconButton>
    //   ),
    // },
    {
      field: "Action",
      width: 90,
      headerClassName: "header-theme",
      hideSortIcons: true,
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={[
              { value: "edit", label: "Edit" },
              {
                value: "delete",
                label: "Delete",
                isDisabled: row?.active,
                hoverMsg: "Location is in use and cannot be deleted",
              },
            ]}
            onActionClick={(v) => {
              if (v === "edit") handleOpenEditProfileModal(row);
              else openDeleteModal(row?.uuid);
            }}
          />
        );
      },
    },
  ]);

  function handleOpenEditProfileModal(user: any) {
    open();
    setLocationUuid(user?.uuid);
  }
  // const onSubmit = (
  //   values: __type_builder<typeof PROVIDER_GROUP_LOCATION_EDIT_INITIAL_VALUES>
  // ) => {
  //   console.log("location edit data", values);
  // };

  return (
    <Box width={"100%"}>
      {opened && (
        <LocationsDrawer
          open={opened}
          handleCloseModal={close}
          title={`Edit Location For ${pgName}`}
          onSubmit={editLocation}
          shouldGetPrefillList
          prefillSpecialtyList={transformMultiSelectFrontEndVal(
            DEFAULT_UUID_NAME_LIST,
            profileData?.specialities
          )}
          preFillValues={{
            ...locationDetails,
            specialities: transformMultiSelectFrontEndVal(
              DEFAULT_UUID_NAME_LIST,
              locationDetails?.specialities
            ),
            officeHours: unNecessaryFEOfficeHourTrans(
              locationDetails?.officeHours
            ),
          }}
          providerGroupTimezone=""
          locationUuid={locationUuid}
        />
      )}

      <DeleteModal
        w="380px"
        open={openedDeleteModal}
        onClose={closeDeleteModal}
        id={deleteId}
        onConfirmation={(id) =>
          archiveLocation({
            locationId: id as string,
            status: true,
            xTENANTID: params.providerGroupId,
          }).then((res) => {
            refetch();
            return res;
          })
        }
        content={"You want to delete this provider group location"}
      />

      {openedLocationModal && (
        <ProviderLocationIdLayoutModal
          providerSpecialities={profileData?.specialities}
          groupNpiNumber={groupNpi}
          pgName={pgName}
          openedMainLocationModal={openedLocationModal}
          onClose={() => {
            closeLocationsModal();
            refetch();
          }}
          locationId={locationUuid}
        />
      )}
      <Box height={"calc(100vh - 22rem)"}>
        <CustomDataGridV2
          loading={tableDataLoading || isFetching}
          searchString={searchKey}
          onSortModelChange={setGridSorting}
          rowCount={providerGroupLocationList?.data?.page?.totalElements}
          getRowId={(row) => row.uuid}
          rows={providerGroupLocationList?.data?.content ?? []}
          columns={COLUMNS}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
        />
      </Box>
    </Box>
  );
};

export default ProviderGroupLocations;
